import { ApiStatus } from '../../types';

export interface OtherIncomeDetail {
    incomeId?: number
    incomeTypeId?: string
    assetLiabilityId?: number
    assetLiabilityTypeId?: string
    frequency?: string
    amount: number
    description: string
    incomeTypeDescription?: string
    netAmount?: number
}

export interface OtherIncomeDetailUpsertPayload extends OtherIncomeDetail {
    isDirectIncome: boolean;
}

export interface OtherIncomeDeletePayload {
    incomeId: number;
    isDirectIncome: boolean;
}

export interface OtherIncomeEdit extends OtherIncomeDetail {
    isEditMode: boolean;
    recordNumber?: number;
}

export interface OtherIncomeErrorMessage {
    incomeType: string
    frequency: string
    amount: string
    description: string
    onlyNumeric: string
    netAmount: number | null
}

export const otherIncomeDefaultAEM = {
    heading: '{heading placeholder}',
    description: '{description placeholder}',
    addIncome: {
        heading: '{addIncome.heading placeholder}',
        description: '{addIncome.description placeholder}',
        noIncomeHeading: '{addIncome.noIncomeHeading placeholder}',
        noIncomeDescription: '{addIncome.noIncomeDescription placeholder}',
        subHeading: '{addIncome.subHeading placeholder}',
        addIncomeButtonText: '{addIncome.addIncomeButtonText}',
    },
    editIncome: {
        heading: '{editIncome.heading placeholder}',
        description: '{editIncome.description placeholder}',
        subHeading: '{editIncome.subHeading placeholder}',
    },
    noIncome: {
        heading: '{noIncomeHeading placeholder}',
        description: '{noIncomeHeading description}',
        confirmButtonText: '{noIncomeConfirmButtonText}',
        cancelButtonText: '{noIncomeCancelButtonText}',
    },
    subHeading: '{subHeading placeholder}',
    editIncomeButtonText: '{Edit}',
    deleteButtonText: '{Delete}',
    descriptionLabel: '{descriptionLabel placeholder}',
    incomeAmountLabel: '{AmountLabel placeholder}',
    incomeAmountPlaceholder: '$',
    incomeAmountBeforeTaxHelpText: '{incomeAmountBeforeTaxHelpText placeholder}',
    frequencyLabel: '{frequencyLabel placeholder}',
    incomeTypeLabel: '{incomeTypeLabel placeholder}',
    cancelButtonText: '{cancelButtonText}',
    updateButtonText: '{updateButtonText}',
    confirmButtonText: '{confirmButtonText}',
    addIncomeButtonText: '{addIncomeButtonText}',
    defaultCentrelinkText: '{Centrelink Income}',
    incomeTypes: [
        {
            'key': 'Additional salary',
            'value': '96',
        },
        {
            'key': 'Child support payments',
            'value': '88',
        },
        {
            'key': 'Centrelink - family & carer',
            'value': '73-1',
        },
        {
            'key': 'Centrelink - aged pension',
            'value': '73-2',
        },
        {
            'key': 'Centrelink - disability support',
            'value': '73-3',
        },
        {
            'key': 'Centrelink - student allowance',
            'value': '73-4',
        },
        {
            'key': 'Veteran affairs pension',
            'value': '73-5',
        },
        {
            'key': 'Other government benefits',
            'value': '26',
        },
        {
            'key': 'Rental income',
            'value': '17:6',
        },
        {
            'key': 'Dividends',
            'value': '61:55',
        },
        {
            'key': 'Superannuation',
            'value': '55:22',
        },
        {
            'key': 'Other income',
            'value': '16:21',
        },
    ],
    directIncomeTypes: [
        {
            'key': 'Additional salary',
            'value': '96',
        },
        {
            'key': 'Child support payments',
            'value': '88',
        },
        {
            'key': 'Centrelink - family & carer',
            'value': '73-1',
        },
        {
            'key': 'Centrelink - aged pension',
            'value': '73-2',
        },
        {
            'key': 'Centrelink - disability support',
            'value': '73-3',
        },
        {
            'key': 'Centrelink - student allowance',
            'value': '73-4',
        },
        {
            'key': 'Veteran affairs pension',
            'value': '73-5',
        },
        {
            'key': 'Other government benefits',
            'value': '26',
        },
    ],
    'validations': {
        'hardValidations': {
            'description': {
                'defaultErrorMessage': '{description validation required placeholder}',
            },
            'amount': {
                'minValue': 0.01,
                'minValueMessage': '{income amount is too low}',
                'defaultErrorMessage': '{amount validation required placeholder}',
                'negativeValueMessage': '{Amount must be positive}',
            },
            'frequency': {
                'defaultErrorMessage': '{frequency validation required placeholder}',
            },
            'incomeType': {
                'defaultErrorMessage': '{incomeType validation required placeholder}',
            },
        },
    },
    frequency: [
        {
            'key': 'Weekly',
            'value': 'Weekly',
        },
        {
            'key': 'Fortnightly',
            'value': 'Fortnightly',
        },
        {
            'key': 'Monthly',
            'value': 'Monthly',
        },
    ],
};

export const incomeDefaultAEM = {
    incomeType: '',
    otherIncome: otherIncomeDefaultAEM,
    frequency: [
        {
            'key': 'Weekly',
            'value': 'Weekly',
        },
        {
            'key': 'Fortnightly',
            'value': 'Fortnightly',
        },
        {
            'key': 'Monthly',
            'value': 'Monthly',
        },
    ],
    validations: {
        hardValidations: {
            mainIncome: {
                taxSelector: {
                    defaultErrorMessage: '{Select either before or after tax validation required placeholder}',
                },
                amount: {
                    minValue: 0.01,
                    minValueMessage: '{income amount is too low}',
                    defaultErrorMessage: '{Enter amount validation required placeholder}',
                    negativeValueMessage: '{Amount must be positive}',
                },
                frequency: {
                    defaultErrorMessage: '{Select frequency validation required placeholder}',
                },
            },
            otherIncome: {
                description: {
                    defaultErrorMessage: '{description validation required placeholder}',
                },
                amount: {
                    minValue: 0.01,
                    minValueMessage: '{income amount is too low}',
                    defaultErrorMessage: '{amount validation required placeholder}',
                    negativeValueMessage: '{Amount must be positive}',
                },
                frequency: {
                    defaultErrorMessage: '{frequency validation required placeholder}',
                },
                incomeType: {
                    defaultErrorMessage: '{incomeType validation required placeholder}',
                },
            },
            selfEmployedIncome: {
                netProfitBeforeTaxAmount: {
                    defaultErrorMessage: '{incomeType validation required placeholder}',
                },
            },
        },
        softValidations: {
            selfEmployedIncome: {
                netProfitBeforeTaxAmount: {
                    baseValue: '0',
                    baseValueMessage: '{incomeAmount soft validation placeholder}',
                },
            },
        },
    },
};

export const { ...incomeBladeAEM } = incomeDefaultAEM;

export interface OtherIncomeApiPayload {
    incomeDetails: Record<number, OtherIncomeEdit>;
    content: string;
}

export interface OtherIncomeApiEndpoints {
    getIncomeUrl: string;
    addDirectIncomeUrl: string;
    addIndirectIncomeUrl: string;
    updateDirectIncomeUrl: string;
    updateIndirectIncomeUrl: string;
    deleteDirectIncomeUrl: string;
    deleteIndirectIncomeUrl: string;
}

export interface OtherIncomeEndpoints {
    getOtherIncomeUrl: string;
    addDirectIncomeUrl?: string;
    addIndirectIncomeUrl?: string;
    updateDirectIncomeUrl?: string;
    updateIndirectIncomeUrl?: string;
    deleteDirectIncomeUrl?: string;
    deleteIndirectIncomeUrl?: string;
}

export type OtherIncomeContent = typeof otherIncomeDefaultAEM;

export interface OtherIncomeState {
    apiStatus?: ApiStatus;
    apiEndpoints: OtherIncomeEndpoints;
    content: OtherIncomeContent;
    incomeDetails: Record<number, OtherIncomeEdit>;
    incomeApiStatus: ApiStatus;
    isAddMode: boolean;
    lastVisitedItem: {
        id: number | null
        mode: 'add' | 'edit' | null
        isSaved: boolean
    }
}

export type IncomeAEM = typeof incomeDefaultAEM;
export type IncomeBladeAEM = typeof incomeBladeAEM;
